import React from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, Cell } from 'recharts';
import type { TimeDataPoint, PlatformDataPoint } from '../types';
import { useSettings } from '../contexts/SettingsContext';

interface TotalViewsChartProps {
    data: TimeDataPoint[];
}

export const TotalViewsChart: React.FC<TotalViewsChartProps> = ({ data }) => {
    const { settings } = useSettings();
    const isDark = settings.theme === 'dark';
    const textColor = isDark ? '#9ca3af' : '#6b7280';
    const tooltipBg = isDark ? '#1f2937' : '#ffffff';
    const tooltipBorder = isDark ? '#374151' : '#e5e7eb';

    return (
        <div className="mt-4 h-40">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
                    <defs>
                        <linearGradient id="area-chart-gradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0c7ff2" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#0c7ff2" stopOpacity={0}/>
                        </linearGradient>
                    </defs>
                    <Tooltip 
                        contentStyle={{ backgroundColor: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: '0.5rem' }}
                        labelStyle={{ fontWeight: 'bold' }}
                        formatter={(value: any) => [`${(Number(value || 0) / 1000000).toFixed(1)}M`, 'Views']}
                    />
                    <XAxis dataKey="name" tick={{ fill: textColor, fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={(tick) => `${tick / 1000000}M`} tick={{ fill: textColor, fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Area type="monotone" dataKey="views" stroke="#0c7ff2" strokeWidth={3} fillOpacity={1} fill="url(#area-chart-gradient)" />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

interface EngagementChartProps {
    data: (Omit<PlatformDataPoint, 'name'> & { name: string })[];
}

const platformColors: { [key: string]: string } = {
    Instagram: '#8ed0ff', // primary-300
    YouTubeShorts: '#5ab8ff', // primary-400
    TikTok: '#2c9aff' // primary-500
};

export const EngagementChart: React.FC<EngagementChartProps> = ({ data }) => {
     const { settings } = useSettings();
    const isDark = settings.theme === 'dark';
    const textColor = isDark ? '#9ca3af' : '#6b7280';
    const tooltipBg = isDark ? '#1f2937' : '#ffffff';
    const tooltipBorder = isDark ? '#374151' : '#e5e7eb';
    
    return (
        <div className="mt-4 h-40">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                     <XAxis dataKey="name" tick={{ fill: textColor, fontSize: 12 }} axisLine={false} tickLine={false} />
                     <Tooltip 
                        cursor={{fill: isDark ? 'rgba(55, 65, 81, 0.5)' : 'rgba(239, 246, 255, 0.5)'}} 
                        contentStyle={{ backgroundColor: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: '0.5rem' }} 
                     />
                     <Bar dataKey="engagement" radius={[6, 6, 0, 0]}>
                        {data.map((entry, index) => (
                           <Cell key={`cell-${index}`} fill={platformColors[entry.name]} />
                        ))}
                     </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};